/**
 * RTK Query API for Search Operations
 *
 * Provides API endpoints for search functionality using the SearchIndexManager.
 */

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { SearchIndexManager } from '../SearchIndexManager';
import type {
  SearchQuery,
  SearchResults,
  SearchDocument,
  SearchFilter,
  SearchHistoryItem,
  SearchSettings,
  SearchStats,
} from '../types';

// Create a singleton instance of SearchIndexManager
let searchManager: SearchIndexManager | null = null;

const getSearchManager = () => {
  if (!searchManager) {
    searchManager = new SearchIndexManager();
  }
  return searchManager;
};

export const searchApi = createApi({
  reducerPath: 'searchApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Search', 'SearchHistory', 'SearchFilters', 'SearchStats'],
  endpoints: (builder) => ({
    // Perform a search
    search: builder.query<SearchResults, SearchQuery>({
      queryFn: async (query) => {
        try {
          const manager = getSearchManager();
          const results = await manager.search(query);
          return { data: results };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: error instanceof Error ? error.message : 'Search failed',
            },
          };
        }
      },
      providesTags: ['Search'],
    }),

    // Get search suggestions
    getSuggestions: builder.query<string[], { prefix: string; limit?: number }>(
      {
        queryFn: async ({ prefix, limit }) => {
          try {
            const manager = getSearchManager();
            const suggestions = await manager.getSuggestions(prefix, limit);
            return { data: suggestions };
          } catch (error) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error:
                  error instanceof Error
                    ? error.message
                    : 'Failed to get suggestions',
              },
            };
          }
        },
      }
    ),

    // Index a document
    indexDocument: builder.mutation<void, SearchDocument>({
      queryFn: async (document) => {
        try {
          const manager = getSearchManager();
          await manager.indexDocument(document);
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to index document',
            },
          };
        }
      },
      invalidatesTags: ['Search', 'SearchStats'],
    }),

    // Index multiple documents
    indexDocuments: builder.mutation<void, SearchDocument[]>({
      queryFn: async (documents) => {
        try {
          const manager = getSearchManager();
          for (const doc of documents) {
            await manager.indexDocument(doc);
          }
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to index documents',
            },
          };
        }
      },
      invalidatesTags: ['Search', 'SearchStats'],
    }),

    // Remove a document from index
    removeDocument: builder.mutation<void, string>({
      queryFn: async (documentId) => {
        try {
          const manager = getSearchManager();
          await manager.removeDocument(documentId);
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to remove document',
            },
          };
        }
      },
      invalidatesTags: ['Search', 'SearchStats'],
    }),

    // Rebuild the entire index
    rebuildIndex: builder.mutation<void, void>({
      queryFn: async () => {
        try {
          const manager = getSearchManager();
          // Force rebuild by reinitializing
          await manager.initialize();
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to rebuild index',
            },
          };
        }
      },
      invalidatesTags: ['Search', 'SearchStats'],
    }),

    // Get search history
    getSearchHistory: builder.query<SearchHistoryItem[], number | undefined>({
      queryFn: async (limit) => {
        try {
          const manager = getSearchManager();
          // Access storage directly for now
          const storage = (manager as any).storage;
          const history = await storage.getHistory(limit);
          return { data: history };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to get history',
            },
          };
        }
      },
      providesTags: ['SearchHistory'],
    }),

    // Clear search history
    clearSearchHistory: builder.mutation<void, void>({
      queryFn: async () => {
        try {
          const manager = getSearchManager();
          const storage = (manager as any).storage;
          await storage.clearHistory();
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to clear history',
            },
          };
        }
      },
      invalidatesTags: ['SearchHistory'],
    }),

    // Get saved filters
    getSavedFilters: builder.query<SearchFilter[], void>({
      queryFn: async () => {
        try {
          const manager = getSearchManager();
          const storage = (manager as any).storage;
          const filters = await storage.getFilters();
          return { data: filters };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to get filters',
            },
          };
        }
      },
      providesTags: ['SearchFilters'],
    }),

    // Save a filter
    saveFilter: builder.mutation<void, SearchFilter>({
      queryFn: async (filter) => {
        try {
          const manager = getSearchManager();
          const storage = (manager as any).storage;
          await storage.saveFilter(filter);
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to save filter',
            },
          };
        }
      },
      invalidatesTags: ['SearchFilters'],
    }),

    // Remove a filter
    removeFilter: builder.mutation<void, string>({
      queryFn: async (filterId) => {
        try {
          const manager = getSearchManager();
          const storage = (manager as any).storage;
          await storage.removeFilter(filterId);
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to remove filter',
            },
          };
        }
      },
      invalidatesTags: ['SearchFilters'],
    }),

    // Get search settings
    getSearchSettings: builder.query<SearchSettings, void>({
      queryFn: async () => {
        try {
          const manager = getSearchManager();
          const storage = (manager as any).storage;
          const settings = await storage.getSettings();
          return { data: settings };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to get settings',
            },
          };
        }
      },
    }),

    // Update search settings
    updateSearchSettings: builder.mutation<void, Partial<SearchSettings>>({
      queryFn: async (settings) => {
        try {
          const manager = getSearchManager();
          const storage = (manager as any).storage;
          await storage.updateSettings(settings);
          // Update manager settings as well
          (manager as any).settings = {
            ...(manager as any).settings,
            ...settings,
          };
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to update settings',
            },
          };
        }
      },
      invalidatesTags: ['Search'],
    }),

    // Get search statistics
    getSearchStats: builder.query<SearchStats, void>({
      queryFn: async () => {
        try {
          const manager = getSearchManager();
          const stats = await manager.getStatistics();
          return { data: stats };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to get statistics',
            },
          };
        }
      },
      providesTags: ['SearchStats'],
    }),

    // Export search index
    exportSearchIndex: builder.query<string, void>({
      queryFn: async () => {
        try {
          const manager = getSearchManager();
          const indexData = await manager.exportIndex();
          return { data: JSON.stringify(indexData) };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to export index',
            },
          };
        }
      },
    }),

    // Import search index
    importSearchIndex: builder.mutation<void, string>({
      queryFn: async (indexDataString) => {
        try {
          const manager = getSearchManager();
          const indexData = JSON.parse(indexDataString);
          await manager.importIndex(indexData);
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to import index',
            },
          };
        }
      },
      invalidatesTags: ['Search', 'SearchStats'],
    }),

    // Clear all search data
    clearSearchData: builder.mutation<void, void>({
      queryFn: async () => {
        try {
          const manager = getSearchManager();
          const storage = (manager as any).storage;
          await storage.clearAll();
          // Reinitialize manager
          await manager.initialize();
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error:
                error instanceof Error ? error.message : 'Failed to clear data',
            },
          };
        }
      },
      invalidatesTags: [
        'Search',
        'SearchHistory',
        'SearchFilters',
        'SearchStats',
      ],
    }),
  }),
});

// Export hooks
export const {
  useSearchQuery,
  useLazySearchQuery,
  useGetSuggestionsQuery,
  useLazyGetSuggestionsQuery,
  useIndexDocumentMutation,
  useIndexDocumentsMutation,
  useRemoveDocumentMutation,
  useRebuildIndexMutation,
  useGetSearchHistoryQuery,
  useClearSearchHistoryMutation,
  useGetSavedFiltersQuery,
  useSaveFilterMutation,
  useRemoveFilterMutation,
  useGetSearchSettingsQuery,
  useUpdateSearchSettingsMutation,
  useGetSearchStatsQuery,
  useExportSearchIndexQuery,
  useImportSearchIndexMutation,
  useClearSearchDataMutation,
} = searchApi;
